import type {
  VisitDistribution,
  VisitFreshness,
  VisitMetrics,
  VisitQuery,
  VisitsAccess,
  VisitGroup,
} from "../shared/visits.ts";

/** Presentation only. None of these helpers fetches, persists or changes domain values. */
export function formatVisitNumber(
  value: number | null | undefined,
  decimals = 0,
): string {
  if (value == null || !Number.isFinite(value)) return "Indisponível";
  const digits = Number.isFinite(decimals)
    ? Math.min(20, Math.max(0, Math.trunc(decimals)))
    : 0;
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export type VisitComposition = {
  state: "ready" | "empty" | "unavailable" | "inconsistent";
  segments: Array<{
    key: "visited" | "pending" | "analysis";
    value: number;
    /** Geometry only; never a replacement for the server's coverage metric. */
    share: number;
  }>;
};

export function compositionOf(
  metrics?: VisitMetrics | null,
  state?: VisitFreshness["state"],
): VisitComposition {
  if (!metrics || state === "partial" || state === "unavailable")
    return { state: "unavailable", segments: [] };
  const keys = ["visited", "pending", "analysis"] as const;
  const values = [metrics.uniqueAddresses, ...keys.map((key) => metrics[key])];
  if (values.some((value) => value == null))
    return { state: "unavailable", segments: [] };
  if (
    values.some((value) => !Number.isSafeInteger(value) || value! < 0) ||
    metrics.visited! + metrics.pending! + metrics.analysis! !==
      metrics.uniqueAddresses
  )
    return { state: "inconsistent", segments: [] };
  if (metrics.uniqueAddresses === 0) return { state: "empty", segments: [] };
  return {
    state: "ready",
    segments: keys.map((key) => ({
      key,
      value: metrics[key]!,
      share: (metrics[key]! / metrics.uniqueAddresses!) * 100,
    })),
  };
}

export type VisitDistributionOrder =
  | "pending"
  | "coverage_desc"
  | "coverage_asc"
  | "name";
const names = new Intl.Collator("pt-BR", {
  sensitivity: "base",
  numeric: true,
});
function byName(a: VisitDistribution, b: VisitDistribution): number {
  return (
    names.compare(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}
function compareNumbers(
  a: number | null,
  b: number | null,
  descending: boolean,
): number {
  const left = a !== null && Number.isFinite(a),
    right = b !== null && Number.isFinite(b);
  if (!left || !right) return left ? -1 : right ? 1 : 0;
  return descending ? b! - a! : a! - b!;
}

export function sortVisitDistribution(
  items: readonly VisitDistribution[],
  order: VisitDistributionOrder,
): VisitDistribution[] {
  return [...items].sort((a, b) => {
    if (order === "name") return byName(a, b);
    const main =
      order === "pending"
        ? compareNumbers(a.metrics.pending, b.metrics.pending, true)
        : compareNumbers(
            a.metrics.coverage,
            b.metrics.coverage,
            order === "coverage_desc",
          );
    return main || byName(a, b);
  });
}

/** Formats an already calculated B−A delta. It never derives relative growth. */
export function formatVisitDifference(
  value: number | null | undefined,
  coverage = false,
): string {
  if (
    value == null ||
    !Number.isFinite(value) ||
    (!coverage && !Number.isSafeInteger(value))
  )
    return "Indisponível";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  const magnitude = Math.abs(value);
  const digits = coverage && magnitude > 0 && magnitude < 0.01 ? 6 : 2;
  const text = coverage
    ? new Intl.NumberFormat("pt-BR", { maximumFractionDigits: digits }).format(
        magnitude,
      )
    : formatVisitNumber(magnitude);
  // A tiny, nonzero percentage-point difference must not look like an exact zero.
  const number = coverage && magnitude > 0 && text === "0" ? "<0,000001" : text;
  const unit = coverage ? "p.p." : magnitude === 1 ? "endereço" : "endereços";
  return `${sign}${number} ${unit}`;
}

/** A tab is local to its source; a repeated tab id never becomes a global filter. */
export function constrainVisitTab(
  query: VisitQuery,
  sources: VisitsAccess["sources"],
  previousSourceId = query.sourceId,
): VisitQuery {
  const source = sources.find((item) => item.id === query.sourceId);
  const valid =
    !!query.sourceId &&
    previousSourceId === query.sourceId &&
    source?.tabs.some((tab) => tab.id === query.tabId);
  return { ...query, tabId: valid ? query.tabId : undefined };
}

export function constrainVisitSubgroup(
  query: VisitQuery,
  groups: readonly VisitGroup[],
  previousGroupId = query.groupId,
): VisitQuery {
  const sameParent = !!query.groupId && previousGroupId === query.groupId;
  const valid =
    sameParent &&
    (query.subgroupId === "__unmapped__" ||
      groups.some(
        (group) =>
          group.id === query.subgroupId && group.parentId === query.groupId,
      ));
  return { ...query, subgroupId: valid ? query.subgroupId : undefined };
}
